import { useState, useEffect } from 'react';

import {
    initialUnpaywallPdfResult,
    parseUnpaywallPdfResponse,
    unknownUnpaywallPdfResult,
    unpaywallRequestKey,
    type UnpaywallPdfResult,
} from '../lib/pdfAvailability';

interface UnpaywallRequestState extends UnpaywallPdfResult {
    requestKey: string;
}

const UNPAYWALL_TIMEOUT_MS = 10_000;

/**
 * Checks the Unpaywall API for an open-access PDF when enabled and the paper
 * has a DOI but no pdf_url in our database. PubMed disables this client-side
 * check in favor of the backend-owned availability decision.
 */
export function useUnpaywallCheck(
    doi: string | null | undefined,
    hasPdfUrl: boolean,
    enabled = true,
): UnpaywallPdfResult {
    const normalizedDoi = doi?.trim() || null;
    const requestKey = unpaywallRequestKey({ doi, hasPdfUrl, enabled });
    const currentInputResult = initialUnpaywallPdfResult({ doi, hasPdfUrl, enabled });
    const [requestState, setRequestState] = useState<UnpaywallRequestState>(() => ({
        requestKey,
        ...currentInputResult,
    }));

    // Effects run after render. Fail closed against a new request key during
    // that intervening render instead of exposing the previous paper's URL.
    const result = requestState.requestKey === requestKey
        ? requestState
        : currentInputResult;

    useEffect(() => {
        const initialResult = initialUnpaywallPdfResult({ doi, hasPdfUrl, enabled });
        setRequestState({ requestKey, ...initialResult });

        if (initialResult.status !== 'checking' || !normalizedDoi) {
            return;
        }

        let cancelled = false;
        const encodedDoi = encodeURIComponent(normalizedDoi);
        const controller = typeof AbortController !== 'undefined'
            ? new AbortController()
            : null;
        const timeout = controller
            ? setTimeout(() => controller.abort(), UNPAYWALL_TIMEOUT_MS)
            : null;

        async function check() {
            try {
                const res = await fetch(
                    `https://api.unpaywall.org/v2/${encodedDoi}?email=hello@paperzilla.ai`,
                    controller ? { signal: controller.signal } : undefined,
                );

                if (cancelled) return;

                if (!res.ok) {
                    setRequestState({ requestKey, ...unknownUnpaywallPdfResult() });
                    return;
                }

                const data = await res.json();
                if (cancelled) return;
                setRequestState({
                    requestKey,
                    ...parseUnpaywallPdfResponse(data),
                });
            } catch {
                if (!cancelled) {
                    // Provider/network errors do not prove that a paper is
                    // closed access or lacks a downloadable PDF.
                    setRequestState({ requestKey, ...unknownUnpaywallPdfResult() });
                }
            } finally {
                if (timeout) clearTimeout(timeout);
            }
        }

        void check();

        return () => {
            cancelled = true;
            controller?.abort();
            if (timeout) clearTimeout(timeout);
        };
    }, [doi, enabled, hasPdfUrl, normalizedDoi, requestKey]);

    return result;
}
